import { ReactNode, useState } from "react";
import styled from "styled-components";
import { Flex, Text } from "rebass/styled-components";
import { TbChevronDown } from "react-icons/tb";
import { IoLockClosed } from "react-icons/io5";

interface ICollapsibleSectionProps {
  title: string;
  titleAccessory?: ReactNode;
  icon?: ReactNode;
  defaultExpanded?: boolean;
  children: ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  locked?: boolean;
  lockedContent?: ReactNode;
  lockedAccentColor?: string;
  lockedTierLabel?: string;
  onLockedClick?: () => void;
}

export const CollapsibleSection = ({
  title,
  titleAccessory,
  icon,
  defaultExpanded = false,
  children,
  expanded: controlledExpanded,
  onToggle,
  locked,
  lockedContent,
  lockedAccentColor,
  lockedTierLabel,
  onLockedClick,
}: ICollapsibleSectionProps) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    if (locked) {
      onLockedClick?.();
      return;
    }
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  return (
    <SectionContainer>
      <SectionHeader onClick={handleToggle} $locked={locked}>
        <TitleRow>
          {icon && <IconWrapper $locked={locked}>{icon}</IconWrapper>}
          <Text
            variant="text.labelSmall"
            style={{
              fontWeight: 600,
              opacity: locked ? 0.5 : 1,
            }}
          >
            {title}
          </Text>
          {titleAccessory}
        </TitleRow>
        {locked ? (
          <LockedBadge $accentColor={lockedAccentColor}>
            <IoLockClosed size={11} />
            <span>
              Unlock with{" "}
              <TierName $color={lockedAccentColor}>{lockedTierLabel}</TierName>
            </span>
          </LockedBadge>
        ) : (
          <ChevronIcon $expanded={expanded} />
        )}
      </SectionHeader>
      {locked && lockedContent ? (
        <LockedContent>{lockedContent}</LockedContent>
      ) : (
        <SectionContent $expanded={expanded}>{children}</SectionContent>
      )}
    </SectionContainer>
  );
};

const SectionContainer = styled.div`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};

  &:last-child {
    border-bottom: none;
  }
`;

const SectionHeader = styled(Flex)<{ $locked?: boolean }>`
  align-items: center;
  cursor: pointer;
  justify-content: space-between;
  padding: 16px 24px;
  transition: background-color 150ms ease;
  user-select: none;

  &:hover {
    background-color: ${({ theme }) => theme.colors.chalkAlpha05};
  }
`;

const TitleRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const IconWrapper = styled.span<{ $locked?: boolean }>`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  display: flex;
  font-size: 16px;
  opacity: ${({ $locked }) => ($locked ? 0.5 : 1)};
`;

const ChevronIcon = styled(TbChevronDown)<{ $expanded: boolean }>`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  height: 18px;
  transition: transform 200ms ease;
  transform: rotate(${({ $expanded }) => ($expanded ? "0deg" : "-90deg")});
  width: 18px;
`;

const LockedBadge = styled.span<{ $accentColor?: string }>`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  display: inline-flex;
  font-family: Barlow, sans-serif;
  font-size: 11px;
  font-weight: 500;
  gap: 5px;
`;

const TierName = styled.span<{ $color?: string }>`
  color: ${({ $color, theme }) => $color || theme.colors.envy};
  font-weight: 700;
`;

const SectionContent = styled.div<{ $expanded: boolean }>`
  display: ${({ $expanded }) => ($expanded ? "block" : "none")};
  padding: 0 16px 18px;
`;

const LockedContent = styled.div`
  padding: 0 16px 18px;
`;
